import React from "react";
import { useRejectBayHirePayment } from "@features/bay-hire/hooks/useBayHireMutations";
import { useTranslation } from "react-i18next";
import ReasonDialog from "./ReasonDialog";

interface RejectPaymentDialogProps {
  open: boolean;
  requestId: string;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

/**
 * DFT-0014 (F-05 wireframe, AD-16, AD-19): Operations rejects a submitted
 * payment receipt with a mandatory reason. Mirrors RejectDialog.
 *
 * DFT-0044 (FE-55, AC39 decision): copy converges onto RejectDialog's own keys
 * (reject-dialog-title/reject-reason-label/reject) - "one action, one label" across every status,
 * same convention FE-78 already applied to the cancel-label family. The underlying mutation
 * (useRejectBayHirePayment, BFF `:reject-payment`) and testIdPrefix stay distinct - only the
 * user-visible copy merges.
 */
const RejectPaymentDialog: React.FC<RejectPaymentDialogProps> = ({
  open,
  requestId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const rejectPaymentMutation = useRejectBayHirePayment();

  const handleSubmit = async (reason: string) => {
    await rejectPaymentMutation.mutateAsync({ id: requestId, request: { reason } });
    await onSuccess();
  };

  return (
    <ReasonDialog
      open={open}
      title={t("bay-hire.reject-dialog-title")}
      reasonLabel={t("bay-hire.reject-reason-label")}
      submitLabel={t("bay-hire.reject")}
      testIdPrefix="reject-payment"
      requestId={requestId}
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  );
};

export default RejectPaymentDialog;
